// @story HIR-TC1
// The exit filter: candidates marked below a stage's pass mark for the first
// time, waiting for a named exit-filter conductor (server: services/
// exitFilter.service.js). They are not out and have been told nothing.
//
// HR sees the queue read-only. A conductor — someone the school named in its
// Hiring settings; the server checks it, this page only follows its answer —
// gets Return, Redirect and Reject, each with a reason of at least 12
// characters. Nothing here messages a candidate.
import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { Button, Card, EmptyState, Icon, StatusPill, Table } from '../ds';
import { Page, PageHeader, Section, Loading, Note } from '../components/page';

const REASON_MIN = 12;
const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const field = { width: '100%', border: '1px solid var(--border-1, #ddd)', borderRadius: 6, padding: '6px 8px', font: 'inherit', fontSize: 'var(--fs-caption)' };

/* The three decisions for one waiting candidate. */
function Decide({ row, vacancies, onDone }) {
  const [reason, setReason] = useState('');
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const short = reason.trim().length < REASON_MIN;

  const send = async (decision) => {
    if (short) { setErr(`Give a reason of at least ${REASON_MIN} characters.`); return; }
    if (decision === 'redirect' && !target) { setErr('Choose the vacancy to redirect them to.'); return; }
    setBusy(decision); setErr('');
    try {
      await api.post(`/exit-filter/${row.resumeId}/stages/${row.stageId}/decision`, {
        decision, reason: reason.trim(), ...(decision === 'redirect' ? { targetJobId: target } : {}),
      });
      onDone();
    } catch (e) {
      setErr(e.response?.data?.message || 'Could not record that just now.');
    } finally { setBusy(''); }
  };

  return (
    <div style={{ display: 'grid', gap: 6, minWidth: 260, whiteSpace: 'normal' }}>
      <textarea aria-label="Reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
        placeholder={`Reason (at least ${REASON_MIN} characters)`} style={field} />
      <select aria-label="Redirect to" value={target} onChange={(e) => setTarget(e.target.value)} style={field}>
        <option value="">Redirect to… (another vacancy)</option>
        {vacancies.filter((v) => v._id !== row.jobId).map((v) => <option key={v._id} value={v._id}>{v.title}</option>)}
      </select>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <Button size="sm" onClick={() => send('return')} disabled={!!busy}>Return</Button>
        <Button size="sm" variant="secondary" onClick={() => send('redirect')} disabled={!!busy}>Redirect</Button>
        <Button size="sm" variant="ghost" onClick={() => send('reject')} disabled={!!busy}>Reject</Button>
      </div>
      {err && <span role="alert" style={{ color: 'var(--status-danger-text)', fontSize: 'var(--fs-caption)' }}>{err}</span>}
    </div>
  );
}

export default function ExitFilter() {
  const [data, setData] = useState(null);
  const [vacancies, setVacancies] = useState([]);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    api.get('/exit-filter/queue')
      .then((r) => setData(r.data?.data || { canDecide: false, rows: [] }))
      .catch((e) => { setData({ canDecide: false, rows: [] }); setError(e.response?.data?.message || 'Could not load the exit filter just now.'); });
  }, []);

  useEffect(load, [load]);
  useEffect(() => {
    if (!data?.canDecide) return;
    api.get('/jobs', { params: { limit: 100 } }).then((r) => {
      const list = r.data?.data?.jobs || r.data?.jobs || r.data?.data || [];
      setVacancies(Array.isArray(list) ? list.map((j) => ({ _id: j._id, title: j.title })) : []);
    }).catch(() => setVacancies([]));
  }, [data?.canDecide]);

  const header = (
    <PageHeader
      title="Exit filter"
      subtitle="Candidates marked below a stage's pass mark for the first time. They are not out and have not been told anything; the school's exit-filter conductor returns, redirects or rejects them."
    />
  );
  if (data === null) return <Page>{header}<Card><Loading label="Loading the exit filter" /></Card></Page>;

  const columns = [
    { key: 'candidate', label: 'Candidate', render: (r) => (
      <div>
        <div style={{ fontWeight: 500, color: 'var(--text-1)' }}>{r.candidateName}</div>
        <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-caption)' }}>{r.jobTitle} · {r.stageLabel}</div>
      </div>
    ) },
    { key: 'mark', label: 'Mark', render: (r) => (
      <StatusPill tone="warning" dot={false}><span className="num">{r.score}%</span><span style={{ opacity: 0.7, fontWeight: 400 }}> / {r.passingScore}%</span></StatusPill>
    ) },
    { key: 'since', label: 'Waiting since', render: (r) => fmt(r.enteredAt) },
    ...(data.canDecide ? [{ key: 'decide', label: 'Decision', render: (r) => <Decide row={r} vacancies={vacancies} onDone={load} /> }] : []),
  ];

  return (
    <Page>
      {header}
      {error && <Note tone="danger">{error}</Note>}
      {!data.canDecide && !error && (
        <Note tone="info">You can see this queue. Only the school's named exit-filter conductors can decide; a recruitment administrator names them.</Note>
      )}
      {data.rows.length === 0 && !error && (
        <Card>
          <EmptyState icon={<Icon name="funnel" size={36} />} title="Nobody waiting" description="No candidate is waiting in the exit filter." />
        </Card>
      )}
      {data.rows.length > 0 && (
        <Section title="Waiting" subtitle={`${data.rows.length} candidate${data.rows.length === 1 ? '' : 's'}`} padding="20px 0 8px">
          <Table columns={columns} rows={data.rows} getRowId={(r) => `${r.resumeId}:${r.stageId}`} />
        </Section>
      )}
    </Page>
  );
}
